'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';

interface Results {
  timeSavedMonthly: number;
  latePaymentReduction: number;
  annualValueRecovered: number;
  breakEvenDays: number;
}

function calculateROI(units: number, hoursPerWeek: number): Results {
  const timeSavedMonthly = Math.round(hoursPerWeek * 0.68 * 4.3);
  const latePaymentReduction = Math.min(Math.round(12 + units * 0.15), 47);
  const annualValueRecovered = Math.round(units * 38 * (latePaymentReduction / 100) * 12);
  const breakEvenDays = Math.max(4, Math.round(30 - units * 0.05));
  return { timeSavedMonthly, latePaymentReduction, annualValueRecovered, breakEvenDays };
}

export default function ROICalculator() {
  const [units, setUnits] = useState(80);
  const [hoursPerWeek, setHoursPerWeek] = useState(14);
  const results = useMemo(() => calculateROI(units, hoursPerWeek), [units, hoursPerWeek]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="calculator" className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden">
      {/* Background accent */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(124,58,237,0.06) 0%, transparent 70%)',
        }}
      />

      <div ref={sectionRef} className="reveal-blur max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[rgba(124,58,237,0.25)] mb-6">
            <span className="text-[10px] font-mono text-violet-light tracking-widest uppercase">ROI Calculator</span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0] leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
          >
            See your numbers before you commit.
          </h2>
          <p className="text-[#9999A8] text-lg max-w-xl mx-auto">
            Enter your portfolio size and how many hours you spend on manual tracking. We&apos;ll show you what you&apos;re leaving on the table.
          </p>
        </div>

        {/* Calculator card */}
        <div className="bento-card card-shine p-5 sm:p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Inputs */}
            <div className="flex flex-col gap-8">
              {/* Units slider */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-manrope font-semibold text-[#EEEEF0]">
                    Number of units
                  </label>
                  <div className="flex items-center gap-2 bg-[#09090B] border border-[rgba(124,58,237,0.25)] rounded-lg px-3 py-1.5">
                    <input
                      type="number"
                      value={units}
                      min={10}
                      max={500}
                      onChange={(e) => {
                        setUnits(Math.min(500, Math.max(10, Number(e.target.value))));
                      }}
                      className="w-14 bg-transparent text-left text-base font-mono font-bold text-[#EEEEF0] outline-none"
                      aria-label="Number of units"
                    />
                    <span className="text-[12px] font-mono text-[#5A5A6E]">units</span>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min={10}
                    max={500}
                    step={5}
                    value={units}
                    onChange={(e) => {
                      setUnits(Number(e.target.value));
                    }}
                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #7C3AED ${((units - 10) / 490) * 100}%, #2A2A36 ${((units - 10) / 490) * 100}%)`,
                    }}
                    aria-label="Units slider"
                  />
                  <div className="flex justify-between mt-2 text-[10px] font-mono text-[#5A5A6E]">
                    <span>10</span>
                    <span>250</span>
                    <span>500</span>
                  </div>
                </div>
                {/* Tier labels */}
                <div className="flex gap-2 flex-wrap">
                  {[
                    { label: 'Small landlord', range: [10, 50] },
                    { label: 'Mid-market', range: [51, 200] },
                    { label: 'Portfolio firm', range: [201, 500] },
                  ].map((tier) => (
                    <button
                      key={tier.label}
                      onClick={() => {
                        setUnits(Math.round((tier.range[0] + tier.range[1]) / 2));
                      }}
                      className={`px-3 py-1 rounded-full text-[11px] font-mono transition-all ${
                        units >= tier.range[0] && units <= tier.range[1]
                          ? 'bg-[rgba(124,58,237,0.2)] border border-[rgba(124,58,237,0.4)] text-violet-light'
                          : 'bg-[#13131A] border border-[rgba(255,255,255,0.05)] text-[#5A5A6E] hover:border-[rgba(124,58,237,0.2)] hover:text-[#9999A8]'
                      }`}
                    >
                      {tier.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hours slider */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-manrope font-semibold text-[#EEEEF0]">
                    Hours/week on manual tracking
                  </label>
                  <div className="flex items-center gap-2 bg-[#09090B] border border-[rgba(124,58,237,0.25)] rounded-lg px-3 py-1.5">
                    <input
                      type="number"
                      value={hoursPerWeek}
                      min={1}
                      max={60}
                      onChange={(e) => {
                        setHoursPerWeek(Math.min(60, Math.max(1, Number(e.target.value))));
                      }}
                      className="w-16 bg-transparent text-left text-base font-mono font-bold text-[#EEEEF0] outline-none"
                      aria-label="Hours per week"
                    />
                    <span className="text-[12px] font-mono text-[#5A5A6E]">hrs</span>
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min={1}
                    max={60}
                    step={1}
                    value={hoursPerWeek}
                    onChange={(e) => {
                      setHoursPerWeek(Number(e.target.value));
                    }}
                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #7C3AED ${((hoursPerWeek - 1) / 59) * 100}%, #2A2A36 ${((hoursPerWeek - 1) / 59) * 100}%)`,
                    }}
                    aria-label="Hours per week slider"
                  />
                  <div className="flex justify-between mt-2 text-[10px] font-mono text-[#5A5A6E]">
                    <span>1h</span>
                    <span>30h</span>
                    <span>60h</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-1.5 rounded-full bg-violet-light animate-pulse" />
                <span className="text-xs font-mono text-[#9999A8] uppercase tracking-widest">Your projected savings</span>
              </div>

              {/* Result cards */}
              <div className="grid grid-cols-2 gap-3">
                <ResultCard
                  value={`${results.timeSavedMonthly}h`}
                  label="Time saved / month"
                  color="#A78BFA"
                  sub="back in your calendar"
                />
                <ResultCard
                  value={`${results.latePaymentReduction}%`}
                  label="Late payment reduction"
                  color="#22C55E"
                  sub="vs. manual reminders"
                />
                <ResultCard
                  value={`R${results.annualValueRecovered.toLocaleString('en-US')}`}
                  label="Annual value recovered"
                  color="#A78BFA"
                  sub="from faster collections"
                />
                <ResultCard
                  value={`${results.breakEvenDays}d`}
                  label="Break-even timeline"
                  color="#F59E0B"
                  sub="from day of onboarding"
                />
              </div>

              {/* Methodology note */}
              <p className="text-[12px] font-mono text-[#5A5A6E] leading-relaxed mt-2">
                Based on anonymized data from {Math.round(units * 0.8 + 200)} similar portfolios. Actual results vary.
              </p>

              {/* CTA */}
              <a
                href="#demo"
                className="cta-primary mt-2 flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl text-base font-bold tracking-tight shadow-violet-glow"
              >
                See Your Portfolio Live
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultCard({
  value,
  label,
  color,
  sub,
}: {
  value: string;
  label: string;
  color: string;
  sub: string;
}) {
  return (
    <div className="bg-[#13131A] rounded-xl border border-[rgba(255,255,255,0.04)] p-4 flex flex-col gap-1 hover:border-[rgba(124,58,237,0.2)] transition-colors">
      <span className="text-4xl font-manrope font-extrabold tracking-tight" style={{ color }}>
        {value}
      </span>
      <span className="text-sm font-manrope font-semibold text-[#EEEEF0]">{label}</span>
      <span className="text-[12px] font-mono text-[#5A5A6E]">{sub}</span>
    </div>
  );
}